"use client";

import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

interface KpiStripProps {
  overall?: number | null;
  performance?: number | null;
  quality?: number | null;
  trust?: number | null;
  className?: string;
}

function clampScore(score: number): number {
  if (Number.isNaN(score)) return 0;
  return Math.min(100, Math.max(0, Math.round(score)));
}

function formatScore(value?: number | null): string {
  return typeof value === "number" ? String(clampScore(value)) : "–";
}

function getScoreColor(score?: number | null): string {
  if (typeof score !== "number") return "#94a3b8";
  if (score >= 80) return "#10b981";
  if (score >= 60) return "#f59e0b";
  return "#ef4444";
}

function ScoreDonut({ score }: { score?: number | null }) {
  const hasScore = typeof score === "number";
  const value = hasScore ? clampScore(score!) : 0;
  const fillColor = getScoreColor(score);
  const data = [
    { name: "Score", value: hasScore ? value : 0, fill: fillColor },
    {
      name: "Remaining",
      value: Math.max(0, 100 - (hasScore ? value : 0)),
      fill: "transparent",
    },
  ];

  if (!hasScore) {
    return (
      <div className="flex flex-col items-center">
        <div className="flex h-40 w-40 items-center justify-center rounded-full border-2 border-dashed border-border/60">
          <span className="tabular-nums text-5xl font-semibold text-muted-foreground">
            –
          </span>
        </div>
        <span className="mt-2 text-sm font-semibold uppercase tracking-[0.24em] text-muted-foreground">
          Overall
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <div className="relative h-40 w-40">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={78}
              strokeWidth={0}
              cornerRadius={14}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              animationDuration={600}
              animationEasing="ease-out"
            >
              {data.map((entry, index) => (
                <Cell key={`overall-cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="tabular-nums text-5xl font-semibold text-foreground">
            {value}
          </span>
        </div>
      </div>
      <span className="mt-2 text-sm font-bold uppercase tracking-[0.24em] text-muted-foreground">
        Overall
      </span>
    </div>
  );
}

function MetricItem({
  label,
  value,
}: {
  label: string;
  value?: number | null;
}) {
  const text = formatScore(value);
  const accentColor = getScoreColor(value);

  return (
    <div className="flex flex-col items-center justify-center gap-1 text-center">
      <span
        className="tabular-nums text-3xl font-semibold"
        style={{ color: text === "–" ? undefined : accentColor }}
      >
        {text}
      </span>
      <span className="text-sm font-medium text-muted-foreground/80">
        {label}
      </span>
    </div>
  );
}

export function KpiStrip({
  overall,
  performance,
  quality,
  trust,
  className = "",
}: KpiStripProps) {
  return (
    <section
      aria-label="Certified score overview"
      className={`flex w-full flex-col items-center gap-10 ${className}`}
    >
      <ScoreDonut score={overall ?? undefined} />
      <div className="grid w-full max-w-lg grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-2">
        <MetricItem label="Website Quality" value={quality} />
        <MetricItem label="Trust & Security" value={trust} />
        <MetricItem label="PageSpeed" value={performance} />
      </div>
    </section>
  );
}
